/**
 * Packaging OS — LanguageSwitcher (Phase 1N-A skeleton).
 *
 * Pin order: EN AR FA TR (LTR), regardless of the page's reading direction.
 * Source: docs/design/packaging-os/ia-decision-record.md D-5 +
 *         rtl-visual-rules.md §3.1.
 *
 * This is a token-only skeleton — it does NOT load a CSS-in-JS library
 * or styled-components. Inline styles reference CSS custom properties
 * defined in tokens.css.
 */

import { LOCALES, type LocaleCode } from "../i18n/locales";

export interface LanguageSwitcherProps {
  current: LocaleCode;
  onChange: (next: LocaleCode) => void;
  /** Aria label for the group; passed by the caller per locale. */
  groupLabel: string;
}

export function LanguageSwitcher({
  current,
  onChange,
  groupLabel,
}: LanguageSwitcherProps): JSX.Element {
  return (
    <nav
      aria-label={groupLabel}
      // Pill order is stable LTR — never flips.
      style={{
        direction: "ltr",
        unicodeBidi: "isolate",
        display: "inline-flex",
        gap: "var(--space-1)",
        padding: "var(--space-1)",
        backgroundColor: "var(--color-bg-surface-2)",
        borderRadius: "var(--radius-full)",
        border: "var(--border-width-hairline) solid var(--color-border-subtle)",
      }}
    >
      {LOCALES.map((locale) => {
        const isActive = current === locale.code;
        return (
          <button
            key={locale.code}
            type="button"
            lang={locale.lang}
            aria-pressed={isActive}
            onClick={() => onChange(locale.code)}
            style={{
              padding: "var(--space-1) var(--space-3)",
              fontSize: "var(--text-caption-size)",
              fontWeight: "var(--font-weight-medium)",
              color: isActive
                ? "var(--color-accent-primary-fg)"
                : "var(--color-text-secondary)",
              backgroundColor: isActive
                ? "var(--color-accent-primary)"
                : "transparent",
              borderRadius: "var(--radius-full)",
              transition:
                "background-color var(--motion-duration-fast) var(--motion-ease-standard)",
            }}
          >
            {locale.nativeLabel}
          </button>
        );
      })}
    </nav>
  );
}
